import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import { fetchPersonalLinksAll, upsertPersonalLink, deletePersonalLink } from '../../redux/slices/personalLinks';
import deleteIcon from '../../assets/Delete.svg';
import TickBox from '../../Components/TickBox/TickBox';
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';

function PersonalLinksForm({ onClose }) {
  const dispatch = useDispatch();
  const { links, isLoading, error } = useSelector((s) => s.personalLinks);
  const [localLinks, setLocalLinks] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => { dispatch(fetchPersonalLinksAll()); }, [dispatch]);
  useEffect(() => { setLocalLinks(links || []); }, [links]);

  const handleChange = (idx, key, value) => {
    setLocalLinks((prev) => prev.map((l, i) => i === idx ? { ...l, [key]: value } : l));
  };

  const handleSaveAll = async () => {
    const toSave = localLinks || [];
    try {
      const results = await Promise.all(toSave.map((payload) => dispatch(upsertPersonalLink({
        id: payload.id,
        name: payload.name,
        url: payload.url,
        color: payload.color || 'gold',
        sortOrder: Number(payload.sortOrder || 0),
        active: payload.active !== false,
      }, { refresh: false }))));
      const allOk = results.every(r => r && r.success);
      if (allOk) {
        enqueueSnackbar('Personal dashboard links saved', { variant: 'success' });
      } else {
        enqueueSnackbar('Some links failed to save', { variant: 'error' });
      }
    } catch (err) {
      console.error('handleSaveAll error', err);
      enqueueSnackbar('Failed to save personal links', { variant: 'error' });
    } finally {
      // Refresh once after all upserts finish
      dispatch(fetchPersonalLinksAll());
    }
  };

  const handleAdd = () => {
    setLocalLinks((prev) => [...prev, { id: undefined, name: '', url: '', color: 'gold', sortOrder: prev.length, active: true }]);
  };

  const handleDelete = async (idx) => {
    const item = localLinks[idx];
    if (item?.id) {
      try {
        const res = await dispatch(deletePersonalLink(item.id, { refresh: false }));
        if (res && res.success) {
          enqueueSnackbar('Link deleted', { variant: 'success' });
        } else {
          enqueueSnackbar('Failed to delete link', { variant: 'error' });
        }
      } catch (err) {
        console.error('deletePersonalLink error', err);
        enqueueSnackbar('Failed to delete link', { variant: 'error' });
      }
    }
    setLocalLinks((prev) => prev.filter((_, i) => i !== idx));
    // Refresh settings list once after delete
    dispatch(fetchPersonalLinksAll());
  };

  const inputStyle = { height: 28, padding: '4px 6px', border: '1px solid #3C96D4', borderRadius: 6, fontFamily: 'PT Sans', fontSize: 11 };
  const buttonPrimary = { border: 'none', background: '#3C96D4', color: '#fff', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', height: 28, fontSize: 11 };
  const buttonSuccess = { border: 'none', background: '#0B8A00', color: '#fff', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', height: 28, fontSize: 11 };
  const iconButton = { border: '2px solid #D32030', background: '#fff', color: '#D32030', borderRadius: 6, cursor: 'pointer', height: 28, width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 1000000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', border: '2px solid #3C96D4', borderRadius: 10, width: 760, maxWidth: '95%', maxHeight: '82%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10, borderBottom: '1px solid #ECEEF6' }}>
          <div style={{ fontFamily: 'Baloo Bhai', fontSize: 18, fontWeight: 700 }}>Personal Dashboard Links</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSaveAll} style={buttonSuccess} disabled={isLoading}>Save</button>
            <button onClick={onClose} style={buttonPrimary}>Close</button>
          </div>
        </div>

        {error && <div style={{ color: '#D32030', padding: '8px 14px' }}>{String(error)}</div>}

        <div style={{ padding: '6px 10px' }}>
          <button onClick={handleAdd} style={buttonPrimary}>Add Link</button>
        </div>

        <div style={{ padding: '0 10px 10px', overflow: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 3fr 60px 60px 120px', gap: 8,
            alignItems: 'center', fontWeight: 700, background: '#F7FBFF', padding: '6px 8px', border: '1px solid #ECEEF6', borderRadius: 6, fontSize: 12
          }}>
            <div>Name</div>
            <div>URL</div>
            <div>Sort</div>
            <div>Active</div>
            <div style={{ textAlign: 'right' }}>Actions</div>
          </div>

          {/* Rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {localLinks.map((link, idx) => (
              <div key={link.id ?? `new-${idx}`} style={{
                display: 'grid', gridTemplateColumns: '2fr 3fr 60px 60px 120px', gap: 8, alignItems: 'center',
                padding: '6px 8px', border: '1px solid #ECEEF6', borderRadius: 6, background: '#fff'
              }}>
                <input value={link.name || ''} onChange={(e) => handleChange(idx, 'name', e.target.value)} style={inputStyle} />
                <input value={link.url || ''} onChange={(e) => handleChange(idx, 'url', e.target.value)} style={inputStyle} />
                <input type='number' value={link.sortOrder ?? 0} onChange={(e) => handleChange(idx, 'sortOrder', e.target.value)} style={{ ...inputStyle }} />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <TickBox type={link.active !== false} tickClick={() => handleChange(idx, 'active', !(link.active !== false))} coloureis={'#3C96D4'} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleDelete(idx)} style={iconButton} title='Delete'>
                    <img src={deleteIcon} alt='Delete' style={{ width: 14, height: 14, filter: 'invert(31%) sepia(83%) saturate(4236%) hue-rotate(345deg) brightness(96%) contrast(93%)' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isLoading && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(240,240,240,0.7)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <CircularProgress size={34} />
        </div>
      )}
    </div>
  );
}

export default PersonalLinksForm;


